/**
 * The correlated-document references, in the shape the editors bind to and the
 * shape the API takes.
 *
 * Two screens carry them — the creation form and the draft editor — and they
 * have to send the same body: the draft is what promotion turns into the
 * document, so a reference that one screen can write and the other cannot
 * even read back is a reference an operator loses by opening the draft.
 * `lib/payment.js` and `lib/totals.js` exist for the same reason.
 *
 * Only the fields people fill in day to day are on screen: the document
 * number, its date, the CIG and the CUP. The API also stores `numItem`,
 * `codiceCommessaConvenzione` and `riferimentoNumeroLinea` (which line of the
 * invoice a contract applies to). A reference read from a stored draft keeps
 * those in `extra` and writes them back untouched, because a screen that shows
 * four fields has no grounds to delete the other three.
 *
 * Kept free of Svelte imports so it can be unit-tested directly.
 */

/** The five reference blocks FatturaPA allows, in the order the API stores them. */
export const CORRELATI_TYPES = ['ordineAcquisto', 'contratto', 'convenzione', 'ricezione', 'fattureCollegate'];

/** One empty reference row, as the editors start. */
export function emptyCorrelato() {
  return { tipo: 'contratto', idDocumento: '', data: '', codiceCIG: '', codiceCUP: '', extra: {} };
}

const text = (value) => (value == null ? '' : String(value));

/** Whether nothing at all has been typed on this row. */
export function isEmptyCorrelato(ref) {
  return !text(ref.idDocumento).trim() && !ref.data && !text(ref.codiceCIG).trim() && !text(ref.codiceCUP).trim();
}

/**
 * Whether this row names a reference the API can store: a document number or a
 * CIG. The schema makes the number mandatory and the gateway uses the CIG in
 * its place, so either one will do; a date or a CUP alone will not.
 */
export function isUsableCorrelato(ref) {
  return Boolean(text(ref.idDocumento).trim() || text(ref.codiceCIG).trim());
}

/** The rows that will be sent. */
export function filledCorrelati(refs) {
  return (refs || []).filter(isUsableCorrelato);
}

/**
 * The 1-based numbers of the rows that were started and cannot be sent: they
 * would be dropped silently on the way out, so the form says so instead.
 */
export function incompleteCorrelati(refs) {
  return (refs || [])
    .map((ref, i) => (!isUsableCorrelato(ref) && !isEmptyCorrelato(ref) ? i + 1 : 0))
    .filter(Boolean);
}

/**
 * Group the usable rows into the body keys the API expects. Two rows of the
 * same type become an array of two, which is what the XSD allows. A row with a
 * CIG and no number is sent without `idDocumento`: the gateway writes the CIG
 * into it, so the rule lives in one place.
 *
 * @param {Array<object>} refs - editor rows
 * @returns {Object<string, Array<object>>} only the types that have a reference
 */
export function correlatiBody(refs) {
  const out = {};
  for (const ref of filledCorrelati(refs)) {
    const block = { ...(ref.extra || {}) };
    const idDocumento = text(ref.idDocumento).trim();
    if (idDocumento) block.idDocumento = idDocumento;
    if (ref.data) block.data = ref.data;
    if (text(ref.codiceCIG).trim()) block.codiceCIG = text(ref.codiceCIG).trim();
    if (text(ref.codiceCUP).trim()) block.codiceCUP = text(ref.codiceCUP).trim();
    (out[ref.tipo] ||= []).push(block);
  }
  return out;
}

/**
 * All five keys, `null` when a type has no reference. On `PATCH` an omitted key
 * means "keep what is stored" and `null` means "remove it", so a reference the
 * operator deleted on screen has to be named to be deleted on the draft.
 */
export function correlatiPayload(refs) {
  const body = correlatiBody(refs);
  return Object.fromEntries(CORRELATI_TYPES.map((tipo) => [tipo, body[tipo] || null]));
}

/**
 * Read the stored `metadata.datiGenerali` back into editor rows.
 *
 * A document number equal to the CIG is what the gateway wrote because there
 * was none, so it is shown as empty: left in place it would stay the old CIG
 * after the operator corrected the CIG.
 *
 * @param {object|null|undefined} datiGenerali
 * @returns {Array<object>} at least one row, so the editor always has a blank to fill
 */
export function correlatiFromStored(datiGenerali) {
  const rows = [];
  for (const tipo of CORRELATI_TYPES) {
    const blocks = datiGenerali?.[tipo];
    if (!Array.isArray(blocks)) continue;
    for (const block of blocks) {
      const { idDocumento, data, codiceCIG, codiceCUP, ...extra } = block || {};
      rows.push({
        tipo,
        idDocumento: idDocumento && idDocumento === codiceCIG ? '' : text(idDocumento),
        data: data ? String(data).slice(0, 10) : '',
        codiceCIG: text(codiceCIG),
        codiceCUP: text(codiceCUP),
        extra,
      });
    }
  }
  return rows.length ? rows : [emptyCorrelato()];
}
